import { supabase } from "@/lib/supabaseClient";

export interface AdminDiagnostics {
  isAdmin: boolean;
  sourceUsed: string;
  email: string;
  userId: string;
  profileRole: string | null;
  userMetadataRole: string | null;
  appMetadataRole: string | null;
}

const ADMIN_EMAIL_ALLOWLIST = [
  "lucioigorpicanco@gmail.com",
  "luciogiorpicanco@gmail.com"
];

export const adminService = {
  /**
   * Diagnoses and returns the admin status and the source that authorized it.
   */
  async getAdminDiagnostics(user: any | null, profile: any | null): Promise<AdminDiagnostics> {
    const diagnostics: AdminDiagnostics = {
      isAdmin: false,
      sourceUsed: "none",
      email: user?.email || "",
      userId: user?.id || "",
      profileRole: profile?.role || null,
      userMetadataRole: user?.user_metadata?.role || null,
      appMetadataRole: user?.app_metadata?.role || null,
    };

    if (!user || !user.email) {
      return diagnostics;
    }

    const email = user.email.toLowerCase().trim();

    // 1. Check Allowlist (Fallback/Emergency)
    if (ADMIN_EMAIL_ALLOWLIST.includes(email)) {
      diagnostics.isAdmin = true;
      diagnostics.sourceUsed = "email_allowlist";
      return diagnostics;
    }
    
    // Check Env variables if present
    const envEmails = import.meta.env.VITE_ADMIN_EMAILS;
    if (envEmails && typeof envEmails === 'string') {
      const allowedEnv = envEmails.split(',').map((e: string) => e.toLowerCase().trim());
      if (allowedEnv.includes(email)) {
        diagnostics.isAdmin = true;
        diagnostics.sourceUsed = "env_allowlist";
        return diagnostics;
      }
    }

    // 2. Check App Metadata
    if (user.app_metadata?.role === "admin") {
      diagnostics.isAdmin = true;
      diagnostics.sourceUsed = "app_metadata";
      return diagnostics;
    }

    // 3. Check User Metadata
    if (user.user_metadata?.role === "admin") {
      diagnostics.isAdmin = true;
      diagnostics.sourceUsed = "user_metadata";
      return diagnostics;
    }

    // 4. Check Profile
    if (profile?.role === "admin" || profile?.is_admin === true) {
      diagnostics.isAdmin = true;
      diagnostics.sourceUsed = "profile";
      return diagnostics;
    }

    // 5. Check admin_users table (legacy/backend truth)
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from("admin_users")
          .select("id")
          .eq("email", email)
          .maybeSingle();
          
        if (!error && data) {
          diagnostics.isAdmin = true;
          diagnostics.sourceUsed = "admin_users_table";
          return diagnostics;
        }
      } catch (err) {
        console.error("[AdminService] Unexpected error checking admin_users table:", err);
      }
    }

    return diagnostics;
  },

  /**
   * Checks if a user is registered as an administrator.
   * Centralized cascading logic.
   */
  async isCurrentUserAdmin(user: any | null, profile: any | null = null): Promise<boolean> {
    const result = await this.getAdminDiagnostics(user, profile);
    
    console.log("[ADMIN_AUTH_DEBUG]", {
      ...result
    });

    return result.isAdmin;
  }
};
